import { BeneficiaryAggregateBooleanDimension } from '@repo/shared/dtos/beneficiary'
import { ColorPicker } from '@repo/ui/color-picker'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Check, X } from 'lucide-react'
import { useEffect, useMemo } from 'react'

export function BooleanBuckets({
  dimension,
  onChange,
}: {
  dimension: BeneficiaryAggregateBooleanDimension
  onChange: (dimension: BeneficiaryAggregateBooleanDimension) => void
}) {
  const { t } = useT({ keyPrefix: 'admin.insights.form.dimensions.buckets.boolean' })
  const buckets = useMemo(() => dimension.buckets, [dimension])

  // Ensure we always have exactly two buckets (true and false)
  useEffect(() => {
    const trueBucket = buckets.find((b) => b.value === true)
    const falseBucket = buckets.find((b) => b.value === false)

    if (!trueBucket || !falseBucket) {
      const newBuckets = [
        trueBucket || { label: t('valueTrue'), value: true },
        falseBucket || { label: t('valueFalse'), value: false },
      ]
      onChange({
        ...dimension,
        buckets: newBuckets,
      })
    }
  }, [buckets, dimension, onChange, t])

  const updateProperty = (value: boolean, property: 'label' | 'color', newValue: string) => {
    onChange({
      ...dimension,
      buckets: buckets.map((b) => (b.value === value ? { ...b, [property]: newValue } : b)),
    })
  }

  const trueBucket = buckets.find((b) => b.value === true)
  const falseBucket = buckets.find((b) => b.value === false)

  if (!trueBucket || !falseBucket) {
    return null // Will be fixed by useEffect
  }

  return (
    <div className="rounded-lg border border-primary-200 p-4 bg-gray-50 space-y-4">
      <div className="space-y-2">
        <div className="grid [grid-template-columns:2fr_2fr_3rem] gap-2">
          <div className="text-sm font-medium text-primary-600">{t('columns.label')}</div>
          <div className="text-sm font-medium text-primary-600">{t('columns.value')}</div>
          <div className="text-sm font-medium text-primary-600">{t('columns.color')}</div>
          <Input
            id="label-true"
            type="text"
            placeholder={t('labelPlaceholder')}
            value={trueBucket.label}
            onChange={(event) => updateProperty(true, 'label', event.target.value)}
          />
          <div className="flex items-center gap-2 text-sm text-primary-700">
            <Check className="w-4 h-4 text-green-600" />
            {t('valueTrue')}
          </div>
          <ColorPicker
            color={trueBucket.color}
            onChange={(value) => updateProperty(true, 'color', value)}
          />
          <Input
            id="label-false"
            type="text"
            placeholder={t('labelPlaceholder')}
            value={falseBucket.label}
            onChange={(event) => updateProperty(false, 'label', event.target.value)}
          />
          <div className="flex items-center gap-2 text-sm text-primary-700">
            <X className="w-4 h-4 text-red-600" />
            {t('valueFalse')}
          </div>
          <ColorPicker
            color={falseBucket.color}
            onChange={(value) => updateProperty(false, 'color', value)}
          />
        </div>
      </div>
    </div>
  )
}
